'use client'

import { useEffect, useState } from 'react'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { makeQueryClient, setupFocusManager, setupOnlineManager } from '@/lib/query-client'

/**
 * QueryProvider wraps children with TanStack React Query's QueryClientProvider.
 *
 * The QueryClient is created lazily per component instance (i.e. once per
 * browser tab) via useState initializer. This avoids sharing state across
 * requests when the component is rendered on the server.
 *
 * Also wires up the React Query onlineManager and focusManager to browser
 * events for accurate online/offline and background/foreground detection on
 * mobile devices.
 *
 * React Query DevTools are included in development builds and removed
 * automatically in production via tree-shaking.
 */
export function QueryProvider({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(() => makeQueryClient())

  // Set up browser-native event listeners for online/focus detection.
  // Runs once on mount (client-side only).
  useEffect(() => {
    setupOnlineManager()
    setupFocusManager()
  }, [])

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-left" />
    </QueryClientProvider>
  )
}
